import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext.jsx';

const TEST_DESTINATION = 'david@xsitegroup.ca';

function moneyFmt(value, unit = '') {
  if (value == null || value === '') return '—';
  const n = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(n)) return '—';
  return `$${n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${unit}`;
}
function pctFmt(value) {
  if (value == null || value === '') return '—';
  const n = parseFloat(value);
  return Number.isFinite(n) ? `+${n.toFixed(2)}%` : '—';
}
function dateFmt(value) {
  if (!value) return '—';
  return String(value).slice(0, 10);
}

// Mirrors the Quote / Bulloch send-dialog pattern: preview → test-mode
// toggle (default ON) → confirm → success state. `onSent` reloads the
// parent so the stamped price_increase_email_sent date shows up.
export default function PriceIncreaseEmailDialog({ open, customerId, siteId, onClose, onSent }) {
  // Sent from the logged-in user's own mailbox (server: getSenderAddress).
  const { user } = useAuth();
  const senderEmail = user?.email || 'david@xsitegroup.ca';
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [testMode, setTestMode] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(null);

  useEffect(() => {
    if (!open || (!customerId && !siteId)) return;
    setLoading(true);
    setPreviewError(null);
    setSent(null);
    setError(null);
    setTestMode(true);
    // Per-site preview when a siteId is provided; otherwise the
    // legacy per-customer aggregated preview.
    const url = siteId
      ? `/api/subscriptions/site/${siteId}/price-increase-preview`
      : `/api/subscriptions/${customerId}/price-increase-preview`;
    axios.get(url)
      .then((res) => setPreview(res.data))
      .catch((err) => setPreviewError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  }, [open, customerId, siteId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, busy]);

  if (!open) return null;

  async function handleSend() {
    setBusy(true);
    setError(null);
    try {
      const url = siteId
        ? `/api/subscriptions/site/${siteId}/send-price-increase-email`
        : `/api/subscriptions/${customerId}/send-price-increase-email`;
      const res = await axios.post(url, { test_mode: testMode });
      setSent(res.data);
      if (onSent) onSent();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  }

  const recipient = preview
    ? (testMode ? preview.recipients.test : preview.recipients.live)
    : null;

  return (
    <div className="modal-overlay" onMouseDown={(e) => {
      if (e.target === e.currentTarget && !busy) onClose();
    }}>
      <div className="modal-card" role="dialog" aria-modal="true" aria-label="Send Price Increase Email">
        <header className="modal-head">
          <h3>{sent ? 'Price Increase Email Sent' : 'Send Price Increase Email'}</h3>
          <button type="button" className="modal-close" onClick={onClose} disabled={busy} aria-label="Close">×</button>
        </header>

        <div className="modal-body">
          {loading && <p className="modal-sub">Loading preview…</p>}
          {previewError && <p className="error">{previewError}</p>}

          {sent && (
            <>
              {sent.test_mode && (
                <p className="error" style={{
                  background: 'var(--color-warn-bg)',
                  color: 'var(--color-warn)',
                  borderColor: 'var(--color-warn-border)'
                }}>
                  <strong>TEST MODE</strong> — sent to <code>{sent.to}</code> only.
                  No note logged on the customer record.
                </p>
              )}
              <div className="quote-success">
                <div className="quote-success-icon">✓</div>
                <h4>Email Sent</h4>
                <p>
                  Renewal notice emailed to <strong>{sent.to}</strong>{' '}
                  covering <strong>{sent.lines_count}</strong>{' '}
                  product line{sent.lines_count === 1 ? '' : 's'}.
                </p>
                <dl className="quote-success-meta">
                  <div><dt>Subject</dt><dd>{sent.subject}</dd></div>
                  <div><dt>Date Stamped</dt><dd className="cell-mono">{sent.price_increase_email_sent}</dd></div>
                  <div><dt>Resend ID</dt><dd className="cell-mono">{sent.resend_message_id || '—'}</dd></div>
                </dl>
                <div className="form-actions">
                  <button type="button" className="btn-primary" onClick={onClose}>Done</button>
                </div>
              </div>
            </>
          )}

          {!sent && preview && (
            <>
              <p className="modal-sub">
                Emails the client a courtesy renewal-price notice from
                {' '}<code>{senderEmail}</code>, then stamps today's
                date on the subscription record.
              </p>

              <div className="chain-inherit" style={{ marginTop: 0 }}>
                <div className="chain-inherit-head">
                  <span className="chain-inherit-title">
                    {preview.customer.first_name || 'Customer'} ·{' '}
                    <strong>{preview.customer.company || `#${preview.customer.id}`}</strong>
                  </span>
                  <span className="chain-inherit-hint">
                    {preview.customer_since && <>Customer since {preview.customer_since} · </>}
                    Recipient: {recipient || <em>(none on file)</em>}
                  </span>
                </div>
                {preview.renewal_date_long && (
                  <p className="modal-sub" style={{ margin: 'var(--space-2) 0 0' }}>
                    Renewal date: <strong>{preview.renewal_date_long}</strong>
                  </p>
                )}
              </div>

              <h4 className="preview-heading" style={{ marginTop: 'var(--space-4)' }}>
                Active modules ({preview.lines.length})
              </h4>
              {preview.lines.length === 0 ? (
                <p className="empty-tab">
                  No active modules — set an annual price on at least one module first.
                </p>
              ) : (
                <div className="table-wrap" style={{ marginBottom: 'var(--space-3)' }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Module</th>
                        <th style={{ textAlign: 'right' }}>Current</th>
                        <th style={{ textAlign: 'right' }}>Increase</th>
                        <th style={{ textAlign: 'right' }}>New Annual</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.lines.map((l) => (
                        <tr key={l.label}>
                          <td>{l.label}</td>
                          <td className="cell-mono" style={{ textAlign: 'right' }}>{moneyFmt(l.current)}</td>
                          <td className="cell-mono" style={{ textAlign: 'right' }}>{pctFmt(l.pct)}</td>
                          <td className="cell-mono" style={{ textAlign: 'right', fontWeight: 600 }}>{moneyFmt(l.newAnnual)}</td>
                        </tr>
                      ))}
                      <tr>
                        <td colSpan={3} style={{ textAlign: 'right', fontWeight: 600, borderTop: '2px solid var(--color-border-strong)' }}>
                          Total New Annual
                        </td>
                        <td className="cell-mono" style={{ textAlign: 'right', fontWeight: 700, borderTop: '2px solid var(--color-border-strong)' }}>
                          {moneyFmt(preview.new_annual_total)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              <div className={`test-mode-panel test-mode-${testMode ? 'on' : 'off'}`}>
                <label className="test-mode-toggle">
                  <input
                    type="checkbox"
                    checked={testMode}
                    onChange={(e) => setTestMode(e.target.checked)}
                    disabled={busy}
                  />
                  <span className="test-mode-label">Test Mode</span>
                </label>
                {testMode ? (
                  <p className="test-mode-status">
                    Email will be redirected to <strong>{TEST_DESTINATION}</strong>.
                    Date will still be stamped on the subscription, but no
                    note will be logged.
                  </p>
                ) : (
                  <p className="test-mode-status">
                    <strong>LIVE</strong> — email will go to the customer
                    at <strong>{preview.recipients.live || '(none on file)'}</strong>{' '}
                    and a note will be logged.
                  </p>
                )}
              </div>

              {error && <p className="error" style={{ marginTop: 'var(--space-3)' }}>{error}</p>}

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleSend}
                  disabled={busy || !preview.can_send || (!testMode && !preview.recipients.live)}
                >
                  {busy
                    ? 'Sending…'
                    : testMode
                      ? 'Confirm Send (Test)'
                      : 'Confirm Send (LIVE)'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
